import React from 'react';

interface HeaderProps {
  title?: string;
  showScore?: boolean;
  score?: number;
  mode?: 'text' | 'picture';
  onBack?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  title = 'AI or Human?',
  showScore = false,
  score = 0,
  mode = 'text',
  onBack,
}) => {
  return (
    <header className="relative bg-gradient-to-r from-dark-900 to-dark-800 border-b border-primary-500/30 shadow-lg">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-circuit opacity-10"></div>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 relative z-10">
        <div className="flex flex-col space-y-3 sm:space-y-4 lg:flex-row lg:justify-between lg:items-center lg:space-y-0">
          {/* Left side - Logo and Title */}
          <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-4">
            {/* Back Button (if provided) */}
            {onBack && (
              <button
                onClick={onBack}
                className="mr-2 p-2 rounded-lg bg-dark-800/50 hover:bg-dark-700 border border-dark-600 hover:border-primary-500/50 transition-all group"
                aria-label="Back to Menu"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-dark-300 group-hover:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            )}

            {/* Logo/Icon - smaller on mobile */}
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center shadow-neon">
              <div className="w-4 h-4 sm:w-6 sm:h-6 border-2 border-white rounded-sm relative">
                <div className="absolute inset-1 bg-white rounded-xs animate-pulse"></div>
              </div>
            </div>

            {/* Title with glitch effect - responsive text size */}
            <div className="relative">
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-display font-bold text-primary-400 tracking-wider uppercase" data-text={title}>
                {title}
              </h1>
              <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 animate-glow"></div>
            </div>
          </div>

          {/* Right side - Score and Daily Average */}
          <div className="flex flex-col xs:flex-row items-center xs:items-center space-y-2 xs:space-y-0 xs:space-x-2 sm:space-x-4 w-full lg:w-auto">

            {/* Current Score Display */}
            {showScore && (
              <div className="relative w-auto">
                <div className="bg-gradient-to-r from-dark-800 to-dark-700 border border-primary-500/50 px-3 sm:px-4 py-2 rounded-lg shadow-neon">
                  <div className="flex items-center space-x-2">
                    {/* Status indicator */}
                    <div className="w-2 h-2 bg-primary-400 rounded-full animate-pulse"></div>

                    <span className="font-mono text-xs sm:text-sm text-primary-300 tracking-wider">
                      Score: {score}
                    </span>
                  </div>
                </div>

                {/* Corner accents - smaller on mobile */}
                <div className="absolute -top-1 -left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-t-2 border-primary-400"></div>
                <div className="absolute -top-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-t-2 border-primary-400"></div>
                <div className="absolute -bottom-1 -left-1 w-2 h-2 sm:w-3 sm:h-3 border-l-2 border-b-2 border-primary-400"></div>
                <div className="absolute -bottom-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 border-r-2 border-b-2 border-primary-400"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;